import React, { useState, useEffect, useContext } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom"
import { login } from "../../actions/userActions";
import MainScreen from "../../components/MainScreen";
import "./LoginScreen.css";
import { useAuth, hasAuthParams } from "react-oidc-context";
import { ReactComponent as Logo } from '..//../media/stetho.svg';
import { Spin, Button } from 'antd';
import { useGlobalConfig } from "../../GlobalConfigContext";
import { deconnexionEnCours, terminerDeconnexion } from "../../utils/logoutBroadcast";

const SpinnerLogo = <Logo id="beatingLogo" style={{ fontSize: 94 }}/>;

function LoginScreen({ history }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);

  const config = useGlobalConfig();

  const cleanAuth = (auth) => {
    localStorage.removeItem("userInfo");
    if (auth) {
      auth.removeUser();
      localStorage.removeItem('accessToken');
    }
  };

  // Authentification keycloak
  useEffect(() => {
    const executeLogin = async () => {
      try {
        setLoading(true);
        await dispatch(login(auth, setLoading));
        // returnUrl est posé par PrivateRoute dans le state OIDC quand un lien profond
        // vers une page protégée a déclenché la connexion. previousPage reste prioritaire.
        history.push(queryParameters.get("previousPage") || auth.user?.state?.returnUrl || "/visie");
      } catch (error) {
        console.error("Erreur de login", error);
        cleanAuth(auth);
        setErrorMsg("" + error);
      }
    };

    // Deux attentes légitimes seulement : l'initialisation de react-oidc-context (isLoading
    // sans navigateur actif — on ignore encore s'il existe un utilisateur stocké) et une
    // redirection déjà partie. On n'attend surtout PAS un signinSilent de fond lancé par App
    // sur la page précédente : arriver ici est une demande explicite de connexion, et sa
    // garde ajoutait jusqu'à 10 s (le timeout d'iframe) avant de laisser partir la
    // redirection. signinRedirect provoque une navigation complète qui emporte cette iframe.
    const initialisation = auth.isLoading && !auth.activeNavigator;
    const redirectionEnCours = auth.activeNavigator === "signinRedirect";
    // deconnexionEnCours : arriver ici pendant qu'un autre onglet se déconnecte ne doit pas
    // relancer une connexion sur le cookie SSO encore vivant. Seul un clic explicite sur
    // « Se reconnecter » lève le verrou.
    if (!hasAuthParams() && (!auth.isAuthenticated || auth.error) && !initialisation && !redirectionEnCours && !hasTriedSignin && !deconnexionEnCours()) {
      const previousPage = history.location.state?.from;
      const params = {};
      if (config?.SSO && config.SSO !== "false") {
        params.extraQueryParams = { kc_idp_hint: config.SSO };
      }
      if (previousPage) {
        params.redirect_uri = auth.settings.redirect_uri + "?previousPage=" + (history.location.state?.from ?? "");
      }
      auth.signinRedirect(Object.keys(params).length > 0 ? params : undefined);
      setHasTriedSignin(true);
    }
    if (auth.isAuthenticated && !auth.error && !loading) {
      // Une fois authentifié, récupération des informations de l'utilisateur
      executeLogin();
    }
  }, [auth, hasTriedSignin]);

  return (
  <MainScreen title={auth.error && "Connexion"}>
    <div className="loginContainer">
      <Spin spinning={auth.isLoading} size="large" indicator={SpinnerLogo}>
        {!auth.isAuthenticated && !auth.isLoading && (<Button key="connexion" type="primary" onClick={() => { terminerDeconnexion(); void auth.signinRedirect(); }}>Se reconnecter</Button>)}
        {auth.error && (<div>Une erreur est survenue... {auth.error.message}</div>)}
        {errorMsg && (<div>Une erreur est survenue... {errorMsg}</div>)}
      </Spin>
    </div>
  </MainScreen>
  );
}

export default LoginScreen;
